import { seeThrough } from 'styles/theme/colors';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useKeycloak } from '@react-keycloak/web';
import { NavLink, useLocation } from 'react-router-dom';
import {
  NavBarSortedUp,
  NavBarSortedDown,
  NavRoutesIcon,
  NavConnectionsIcon,
  NavSourcesIcon,
  NavWorkspacesIcon,
} from '../ui/ProjectIcons';
import type { KeycloakTokenParsed } from 'keycloak-js';
import { EulaModal } from './EulaModal';
import { getEula, getMyDetails } from 'workspaces/api';
import SelectInput from '../inputs/SelectInput';
import NotificationsMenu from './NotificationsMenu';
// Import multilanguage utilities as needed
import { useTimezone } from 'shared/context/TimezoneContext';
import { getUtcOffsets } from '../../utils/getUtcOffsets';
import { KEYCLOAK_INTENTIONAL_LOGOUT_KEY } from '../../auth/useKeycloak';
import { useScrollLock } from '../../hooks/useScrollLock';
import { toast } from 'react-toastify';

type ParsedToken = KeycloakTokenParsed & {
  email?: string;
  preferred_username?: string;
  given_name?: string;
  family_name?: string;
};
// Track navbar visibility based on scroll

const getEnvironment = () => {
  const hostname = window.location.hostname;

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return { name: 'local', color: 'local-env-stripe' };
  } else if (hostname.includes('dev.gundiservice.org')) {
    return { name: 'dev', color: 'dev-env-stripe' };
  } else if (hostname.includes('stage.gundiservice.org')) {
    return { name: 'stage', color: 'stage-env-stripe' };
  } else {
    return { name: '', color: '' };
  }
};

const Nav = () => {
  const { t } = useTranslation();
  const { keycloak } = useKeycloak();
  const divRef = useRef<HTMLDivElement | null>(null);
  // Check both localStorage and keycloak.authenticated to handle session expiration
  const isLoggedIn =
    keycloak?.authenticated && localStorage.getItem('userAuthorized') !== null;
  const [timezones, setTimezones] = useState<any[]>([]);
  const { timezone, setTimezone } = useTimezone();
  const [subMenu, setSubMenu] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const closeMobileMenu = () => setMobileMenuOpen(false);
  const [showEULAModal, setShowEULAModal] = useState(false);
  const [visible, setVisible] = useState(true);
  const lastScrollY = useRef(window.pageYOffset);
  const [environment, setEnvironment] = useState({ name: '', color: '' });
  const parsedToken: ParsedToken | undefined = keycloak?.tokenParsed;
  const [displayName, setDisplayName] = useState('');
  // null = unknown (not yet fetched or fetch failed), true/false = known
  const [profileComplete, setProfileComplete] = useState<boolean | null>(null);
  const location = useLocation();

  const handleNavClick = (e: React.MouseEvent) => {
    if (location.pathname === '/account' && profileComplete === false) {
      e.preventDefault();
      toast.warning(
        t('Please fill in your contact information before continuing.'),
        { position: toast.POSITION.TOP_RIGHT },
      );
    }
  };
  useScrollLock(mobileMenuOpen);
  const toggleSubMenu = () => setSubMenu((prev) => !prev);

  const getEulaStatus = React.useCallback(async () => {
    if (keycloak.token) {
      const results = await getEula(keycloak.token);
      if (!results.accepted) {
        setShowEULAModal(true);
      }
    }
  }, [keycloak.token]);

  const loadDisplayName = React.useCallback(async () => {
    if (keycloak.token) {
      setProfileComplete(null);
      const pt: ParsedToken | undefined = keycloak.tokenParsed as
        ParsedToken | undefined;
      const tokenName = [pt?.given_name?.trim(), pt?.family_name?.trim()]
        .filter(Boolean)
        .join(' ');
      try {
        const profile = await getMyDetails(keycloak.token);
        const apiName = [
          profile?.first_name?.trim(),
          profile?.last_name?.trim(),
        ]
          .filter(Boolean)
          .join(' ');
        setDisplayName(
          apiName ||
            tokenName ||
            pt?.preferred_username?.trim() ||
            pt?.email?.trim() ||
            '',
        );
        setProfileComplete(
          profile
            ? !!(
                profile.first_name?.trim() &&
                profile.last_name?.trim() &&
                profile.contact_email?.trim()
              )
            : null,
        );
      } catch {
        setDisplayName(
          tokenName ||
            pt?.preferred_username?.trim() ||
            pt?.email?.trim() ||
            '',
        );
        setProfileComplete(null);
      }
    }
  }, [keycloak.token, keycloak.tokenParsed]);

  const logoutUser = () => {
    // Set flag to indicate intentional logout (prevents showing "Session Expired" modal)
    // Use localStorage so it persists even if sessionStorage is cleared
    localStorage.setItem(KEYCLOAK_INTENTIONAL_LOGOUT_KEY, 'true');
    keycloak.logout({ redirectUri: window.location.origin });
    localStorage.removeItem('userAuthorized');
    sessionStorage.clear();
  };

  useEffect(() => {
    keycloak.onAuthSuccess = () => {
      if (!keycloak.isTokenExpired(0)) {
        localStorage.setItem('userAuthorized', 'true');
        // Clear the intentional logout flag on successful login
        localStorage.removeItem(KEYCLOAK_INTENTIONAL_LOGOUT_KEY);
        keycloak.loadUserProfile();
        getEulaStatus();
        loadDisplayName();
      }
    };

    function handleClickOutside(event: any) {
      if (
        divRef.current &&
        !divRef.current.contains(event.target) &&
        !event.target.closest('a')
      ) {
        setSubMenu(false);
      }
    }

    if (isLoggedIn && !displayName) {
      loadDisplayName();
    }

    let temp = getUtcOffsets();
    setTimezones(temp);

    setEnvironment(getEnvironment());

    const updateNavbarVisibility = () => {
      const currentScrollY = window.pageYOffset;

      // Navbar hide/show on scroll is temporarily disabled.
      // When re-enabling, consider using a scroll threshold to prevent flickering on small movements:
      // const SCROLL_THRESHOLD = 10;
      // const scrollDelta = currentScrollY - lastScrollY.current;
      // if (scrollDelta < -SCROLL_THRESHOLD) {
      //   setVisible(true);
      // } else if (scrollDelta > SCROLL_THRESHOLD) {
      //   setVisible(false);
      // }

      lastScrollY.current = currentScrollY;
    };

    const handleProfileUpdated = (e: Event) => {
      const { first_name, last_name, contact_email } = (e as CustomEvent)
        .detail;
      const apiName = [first_name?.trim(), last_name?.trim()]
        .filter(Boolean)
        .join(' ');
      const tokenName = [
        parsedToken?.given_name?.trim(),
        parsedToken?.family_name?.trim(),
      ]
        .filter(Boolean)
        .join(' ');
      setDisplayName(
        apiName ||
          tokenName ||
          parsedToken?.preferred_username?.trim() ||
          parsedToken?.email?.trim() ||
          '',
      );
      setProfileComplete(
        !!(first_name?.trim() && last_name?.trim() && contact_email?.trim()),
      );
    };

    window.addEventListener('scroll', updateNavbarVisibility);
    window.addEventListener('userProfileUpdated', handleProfileUpdated);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', updateNavbarVisibility);
      window.removeEventListener('userProfileUpdated', handleProfileUpdated);
    };
  }, [keycloak, getEulaStatus, loadDisplayName]);

  const handleSelectTimezone = (e: any) => {
    setTimezone(e);
    localStorage.setItem('selectedTimezone', JSON.stringify(e));
  };

  // One letter, not every initial: a four-part name was rendering as "VALR".
  const nameInitial = displayName.trim()
    ? displayName.trim()[0].toUpperCase()
    : '';
  const shortTimezone =
    timezone?.label.match(/UTC[+-]\d+(?::\d+)?/)?.[0] ?? timezone?.label;
  const showEnvStripe =
    environment.name !== 'production' && environment.name !== '';

  // The rail's four sections, kept as data so the icon-tile + label markup and
  // its active/inactive treatment live in exactly one place. The label testids
  // read "..._label" though they sit on the icon+label stack.
  // Which section owns the current URL. Derived here rather than from
  // NavLink's own isActive because `to="/"` with no `end` matches EVERY path —
  // harmless as an underline in the old top bar, but in the rail it would light
  // up two filled tiles at once. `paths` lists the prefixes each section owns,
  // so "/" and "/routes/:id" both belong to Routes and "/account" to none.
  const railSections = [
    {
      to: '/',
      paths: ['/routes'],
      testId: 'nav_routes_btn',
      labelTestId: 'nav_routes_label',
      label: t('Routes'),
      Icon: NavRoutesIcon,
    },
    {
      to: '/connections',
      paths: ['/connections'],
      testId: 'nav_connections_btn',
      labelTestId: 'nav_connections_label',
      label: t('Connections'),
      Icon: NavConnectionsIcon,
    },
    {
      to: '/sources',
      paths: ['/sources'],
      testId: 'nav_sources_btn',
      labelTestId: 'nav_sources_label',
      label: t('Sources'),
      Icon: NavSourcesIcon,
    },
    {
      to: '/workspaces',
      paths: ['/workspaces'],
      testId: 'nav_organizations_btn',
      labelTestId: 'nav_workspaces_label',
      label: t('Workspaces'),
      Icon: NavWorkspacesIcon,
    },
  ];
  const isSectionActive = (section: (typeof railSections)[number]) =>
    location.pathname === section.to ||
    section.paths.some(
      (p) => location.pathname === p || location.pathname.startsWith(`${p}/`),
    );

  return (
    <div>
      {/* Fixed top strip. On desktop it is only the version banner — the
          navigation itself is the left rail below. Below md it also keeps the
          old top bar (logo + hamburger), so the small-screen layout and the
          drawer are unchanged. */}
      <div
        data-testid="main-nav-wrapper"
        className={`fixed top-0 z-50 w-full transition-transform duration-300 ${visible ? 'translate-y-0' : '-translate-y-full'}`}
      >
        {/* Environment stripe across the top of the screen. Above the mobile bar
            it takes its own row; from md up it overlays the top edge of the bar,
            so the rail and the page below do not move. */}
        {showEnvStripe && (
          <div
            className={`${environment.color} py-1.5 text-center text-body-sm font-bold md:absolute md:inset-x-0 md:top-0 md:z-10 md:h-1.5 md:p-0`}
            aria-hidden="true"
          ></div>
        )}
        {/* Desktop top bar. 70px tall, spanning the full width so the rail
            hangs beneath it. The account controls stay on the page gutter, but
            the logo sits close to the left edge — at the gutter it read as
            floating in from the middle of the bar (user feedback). */}
        <nav className="hidden h-[70px] items-center border-b border-ui-divider-lines bg-white pl-4 pr-8 md:flex desktop:pr-[60px]">
          {/* 40px, not 32: the rail below used to out-shout the wordmark, which
              read as the icons being the brand. The 70px bar has the room. */}
          <img
            src={require('styles/images/gundi_logo.png')}
            alt="gundi logo"
            className="h-10"
          />
          <h3
            translate="no"
            className="ml-2 font-heading text-heading-md font-bold text-ui-text-primary-text"
          >
            {process.env.REACT_APP_TEST_VAR}
          </h3>
          {isLoggedIn && (
            <div className="ml-auto flex flex-row items-center gap-5 text-ui-text-primary-text">
              {/* tz select — compact label at md, full label at lg+ */}
              <div
                className="flex w-[120px] cursor-pointer justify-end min-[1125px]:w-[220px] xl:w-[280px]"
                title={timezone?.label}
              >
                <SelectInput
                  options={timezones}
                  data-testid="select_timezone_activity_logs_filter_compact"
                  className={'basic-single text-body-sm min-[1125px]:hidden'}
                  value={{ label: shortTimezone, value: timezone?.value }}
                  isDisabled={false}
                  isLoading={false}
                  isClearable={false}
                  isSearchable={false}
                  isMulti={false}
                  name={'Filter by Workspace'}
                  onChange={(e: any) => handleSelectTimezone(e)}
                  variant="navTimezone"
                />
                <SelectInput
                  options={timezones}
                  data-testid="select_timezone_activity_logs_filter_full"
                  className={
                    'basic-single hidden text-body-sm min-[1125px]:block'
                  }
                  value={timezone}
                  isDisabled={false}
                  isLoading={false}
                  isClearable={false}
                  isSearchable={true}
                  isMulti={false}
                  name={'Filter by Workspace'}
                  onChange={(e: any) => handleSelectTimezone(e)}
                  variant="navTimezone"
                />
              </div>

              <NotificationsMenu />

              {/* profile dropdown */}
              <div ref={divRef} className="relative flex items-center">
                <button
                  type="button"
                  onClick={toggleSubMenu}
                  className="flex cursor-pointer items-center"
                  data-testid="Nav-user-menu-trigger"
                  aria-expanded={subMenu}
                  title={displayName}
                >
                  {/* The spec sheet's md avatar: a 36px square with 4px
                      corners, Teal T100 under a white initial in bold. The
                      initial is not a heading, so it stays in Roboto. */}
                  <span className="flex h-9 w-9 items-center justify-center rounded bg-teal-t100 text-center text-body-sm font-bold text-white">
                    {nameInitial}
                  </span>
                </button>
                <div
                  className={
                    !subMenu
                      ? 'hidden'
                      : 'absolute right-0 top-full z-30 mt-2 overflow-hidden rounded bg-white shadow-dropdown'
                  }
                >
                  <ul
                    role="menu"
                    className="whitespace-nowrap text-body-md font-normal text-ui-text-primary-text"
                  >
                    <li
                      role="none"
                      className="cursor-pointer hover:bg-teal-t10"
                    >
                      <NavLink
                        role="menuitem"
                        to="/account"
                        data-testid="nav_account_btn"
                        onClick={() => setSubMenu(false)}
                        className="flex h-10 items-center gap-2 px-4"
                      >
                        {t('Account Settings')}
                      </NavLink>
                    </li>
                    <li role="none" className="hover:bg-teal-t10">
                      <button
                        type="button"
                        role="menuitem"
                        data-testid="nav_logout_btn"
                        className="flex h-10 w-full cursor-pointer items-center gap-2 px-4 text-left"
                        onClick={logoutUser}
                      >
                        {t('Log Out')}
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </nav>
        {/* h-20 rather than padding: the app shell has to offset content by this
            bar's height, and an explicit number is one both places can agree
            on. */}
        {/* Same on mobile: logo near the edge, controls on the gutter. */}
        <nav className="w-full bg-white pl-4 pr-8 text-ui-text-primary-text shadow-sm shadow-ui-divider-lines md:hidden">
          <div className="flex h-20 w-full items-center justify-between">
            <div className="flex flex-row items-end">
              <img
                src={require('styles/images/gundi_logo.png')}
                alt="gundi logo"
                className="mr-2 h-8"
              />
              <h3
                translate="no"
                className="-mb-1 mr-6 pb-0 font-heading text-heading-md font-bold text-ui-text-primary-text"
              >
                {process.env.REACT_APP_TEST_VAR}
              </h3>
            </div>
            {/* Hamburger button — visible only on small screens */}
            {isLoggedIn && (
              <button
                className="ml-3 text-ui-text-primary-text"
                onClick={() => setMobileMenuOpen(true)}
                aria-label="Open menu"
                aria-expanded={mobileMenuOpen}
                aria-controls="mobile-menu"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M3 6h18M3 12h18M3 18h18"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            )}
          </div>
        </nav>
      </div>

      {/* Desktop navigation rail — 110px wide, hanging beneath the 70px top
          bar. Pure navigation: the logo, timezone picker and profile menu all
          live in the top bar, so nothing appears in two places. Hidden below
          md, where the hamburger drawer takes over. The app shell offsets page
          content by the same 110px. z-40 so a modal overlay (z-50) covers it. */}
      <aside
        data-testid="Nav-rail"
        // A real border, not a shadow: `shadow-sm` is `0 1px 2px 0` — offset
        // straight down with a 2px blur and no spread — so at the rail's right
        // edge it can only smear about a pixel, and it shows or vanishes
        // depending on what sits behind it. That is why the divider looked like
        // it stopped partway down the page. The top bar already draws its
        // matching edge with `border-b border-ui-divider-lines`.
        className="fixed bottom-0 left-0 top-[70px] z-40 hidden w-[110px] flex-col border-r border-ui-divider-lines bg-white pt-6 md:flex"
      >
        {isLoggedIn && (
          <ul className="flex flex-col items-center gap-8 font-bold">
            {railSections.map((section) => {
              const { to, testId, labelTestId, label, Icon } = section;
              const isActive = isSectionActive(section);
              return (
                <li key={to}>
                  <NavLink
                    data-testid={testId}
                    to={to}
                    onClick={handleNavClick}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    {/* Fixed 94px width so every section's icon+label stack is
                        the same size; the longest labels ("Connections",
                        "Workspaces") fit inside the rail's 110px. */}
                    <span
                      data-testid={labelTestId}
                      className="flex w-[94px] flex-col items-center gap-1"
                    >
                      {/* The active section sits on a 40px rounded square in
                          Brand Accent Green; the others are the bare icon. The box
                          is the same size in both states, so the icon's centre
                          doesn't shift when the section changes.
                          These four glyphs are solid fills, so there is no stroke
                          weight to thin — scale is the only dial. At 36px in a
                          52.595px tile they carried more ink than the wordmark and
                          read as the loudest thing on the screen; 25-in-40 keeps
                          the same proportions at a weight that sits behind the
                          labels instead of in front of them. */}
                      <span
                        className={`flex h-[40px] w-[40px] items-center justify-center rounded-[12px] transition-colors ${isActive ? 'bg-brand-accent-green text-white' : 'text-greyscale-4 hover:bg-teal-t10'}`}
                      >
                        <Icon size={25} />
                      </span>
                      {/* Height follows the text, so the 4px above the label and
                          the 16px below it are the gaps the design asks for — a
                          fixed slot pads both out by its own slack. A label that
                          wraps (Spanish and French write "Workspaces" as two
                          words) makes its own item taller. Strata/Body/SM. */}
                      <span
                        className={`w-full text-center text-body-sm font-normal ${isActive ? 'text-green-3' : 'text-ui-text-secondary-text'}`}
                      >
                        {label}
                      </span>
                    </span>
                  </NavLink>
                </li>
              );
            })}
          </ul>
        )}
      </aside>
      {/* Mobile full-screen menu */}
      {mobileMenuOpen && isLoggedIn && (
        <>
          {/* Backdrop — darkens content behind the drawer */}
          <div
            className="fixed inset-0 z-[59] md:hidden"
            style={{ backgroundColor: seeThrough('greyscale-0', 0.4) }}
            onClick={closeMobileMenu}
            aria-hidden="true"
          />
          <div
            id="mobile-menu"
            className="fixed left-0 right-0 top-0 z-[60] flex max-h-screen flex-col overflow-y-auto bg-white shadow-lg md:hidden"
          >
            {/* Menu header */}
            <div className="flex items-center justify-between py-6 pl-5 pr-8">
              <img
                src={require('styles/images/gundi_logo.png')}
                alt="gundi logo"
                className="h-8"
              />
              <div className="flex items-center gap-8">
                <span className="max-w-[247px] truncate text-body-md font-bold text-ui-text-primary-text">
                  {displayName}
                </span>
                <button
                  onClick={closeMobileMenu}
                  className="text-ui-text-primary-text"
                  aria-label="Close menu"
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path
                      d="M15 5L5 15M5 5l10 10"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
              </div>
            </div>

            {/* Nav links */}
            <ul className="flex-1 space-y-6 px-5 py-6">
              <li>
                <NavLink
                  to="/"
                  onClick={(e) => {
                    handleNavClick(e);
                    if (location.pathname !== '/account') closeMobileMenu();
                  }}
                >
                  {({ isActive }) => (
                    <span
                      className={
                        isActive
                          ? 'text-body-lg font-bold text-green-3'
                          : 'text-body-lg font-bold text-ui-text-primary-text'
                      }
                    >
                      {t('Routes')}
                    </span>
                  )}
                </NavLink>
              </li>
              <li>
                <NavLink
                  to="/connections"
                  onClick={(e) => {
                    handleNavClick(e);
                    if (location.pathname !== '/account') closeMobileMenu();
                  }}
                >
                  {({ isActive }) => (
                    <span
                      className={
                        isActive
                          ? 'text-body-lg font-bold text-green-3'
                          : 'text-body-lg font-bold text-ui-text-primary-text'
                      }
                    >
                      {t('Connections')}
                    </span>
                  )}
                </NavLink>
              </li>
              <li>
                <NavLink
                  to="/sources"
                  onClick={(e) => {
                    handleNavClick(e);
                    if (location.pathname !== '/account') closeMobileMenu();
                  }}
                >
                  {({ isActive }) => (
                    <span
                      className={
                        isActive
                          ? 'text-body-lg font-bold text-green-3'
                          : 'text-body-lg font-bold text-ui-text-primary-text'
                      }
                    >
                      {t('Sources')}
                    </span>
                  )}
                </NavLink>
              </li>
              <li>
                <NavLink
                  to="/workspaces"
                  onClick={(e) => {
                    handleNavClick(e);
                    if (location.pathname !== '/account') closeMobileMenu();
                  }}
                >
                  {({ isActive }) => (
                    <span
                      className={
                        isActive
                          ? 'text-body-lg font-bold text-green-3'
                          : 'text-body-lg font-bold text-ui-text-primary-text'
                      }
                    >
                      {t('Workspaces')}
                    </span>
                  )}
                </NavLink>
              </li>
            </ul>

            {/* Timezone + language + logout at the bottom */}
            <div className="space-y-8 px-5 pb-6">
              <div className="-ml-2">
                <SelectInput
                  options={timezones}
                  className="basic-single text-body-sm"
                  value={timezone}
                  isDisabled={false}
                  isLoading={false}
                  isClearable={false}
                  isSearchable={true}
                  isMulti={false}
                  name="Filter by Workspace"
                  onChange={(e: any) => handleSelectTimezone(e)}
                  variant="navTimezoneMobile"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>
              <NavLink
                to="/account"
                data-testid="nav_mobile_account_btn"
                className="block w-full text-left text-body-lg font-bold text-ui-text-primary-text"
                onClick={closeMobileMenu}
              >
                {t('Account Settings')}
              </NavLink>
              <button
                data-testid="nav_mobile_logout_btn"
                className="w-full text-left text-body-lg font-bold text-ui-text-primary-text"
                onClick={() => {
                  closeMobileMenu();
                  logoutUser();
                }}
              >
                {t('Log Out')}
              </button>
            </div>
          </div>
        </>
      )}

      {showEULAModal && <EulaModal />}
    </div>
  );
};

export default Nav;
